/**
 * Part 19e (decision 76; log principles 1–4, tables MG and MB): the notes an earlier version kept apart, each turned into
 * a note record by one rule wherever it is met: a chapter entry's notes, a character or place note (`bookNotes`) and a
 * Word comment (`comments`). The local store's upgrade to version 10 moves its records through {@link planMove}; a bundle
 * of version 1 to 4 and an earlier build's typing record go through the same functions. Pure: the time and fresh IDs are
 * given.
 */
import { readCommentRecord, readBookNote, readLegacyEntry } from './records.ts';
import type { BookNote, ChapterEntry, CommentRecord, NoteKind, NoteRecord } from './records.ts';

/** MG6: the Notebook group each kind of book note comes over in (the app's words for them). */
export const LEGACY_GROUPS: Readonly<Record<NoteKind, string>> = Object.freeze({ character: 'Characters', place: 'Places' });

/** What every note this module makes shares: the writer's, Only me, a first note, open, standing. */
const FIRST = { visibility: 'only-me', parentId: null, resolved: false, deletedAt: null } as const;

/**
 * MG1–MG3, MB1, MB2: the whole-chapter note a chapter's notes become: its ID `id` (the chapter's, principle 4), the
 * writer's, its text the notes byte for byte, stored and edited `at`; null when there are none (an empty string).
 */
export function chapterNotesNote(id: string, chapter: Pick<ChapterEntry, 'id' | 'bookId'>, notes: string, at: number): NoteRecord | null {
  if (notes === '') return null;
  return { id, bookId: chapter.bookId, chapterId: chapter.id, anchor: null, title: '', group: '', origin: 'writer', author: '', sourceKey: null, ...FIRST, text: notes, writtenAt: null, storedAt: at, editedAt: at };
}

/** MG6, MB3: the Notebook note a character or place note becomes, under `id`: titled by its name, in its kind's group. */
export function bookNoteNote(id: string, note: BookNote): NoteRecord {
  const at = note.createdAt;
  return {
    id,
    bookId: note.bookId,
    chapterId: null,
    anchor: null,
    title: note.name,
    group: LEGACY_GROUPS[note.kind],
    origin: 'writer',
    author: '',
    sourceKey: null,
    ...FIRST,
    text: note.text,
    writtenAt: at,
    storedAt: at,
    editedAt: at,
  };
}

/** MG9, MB4: the Word note a comment becomes, under `id`: on its chapter, anchored as it was, by its author. */
export function commentNote(id: string, comment: CommentRecord): NoteRecord {
  const { bookId, chapterId, range, quote, before, after, author, sourceKey, text, createdAt, storedAt } = comment;
  return {
    id,
    bookId,
    chapterId,
    anchor: { range: range === null ? null : { from: range.from, to: range.to }, quote, before, after },
    title: '',
    group: '',
    origin: 'word',
    author,
    sourceKey,
    ...FIRST,
    text,
    writtenAt: createdAt,
    storedAt,
    editedAt: storedAt,
  };
}

/** A stored record: its key and its value as stored, untrusted. */
export type StoredRow = readonly [key: IDBValidKey, raw: unknown];

/** What the move reads: every record of the three stores it moves from. */
export interface MoveInput {
  readonly comments: readonly StoredRow[];
  readonly bookNotes: readonly StoredRow[];
  readonly entries: readonly StoredRow[];
}

/** What the move writes, all in the upgrade's one transaction (principle 2). */
export interface MovePlan {
  /** The note records to put (MG2, MG6, MG9). */
  readonly notes: readonly NoteRecord[];
  /** The entries to put again without their notes (MG1–MG3, MG5). */
  readonly entries: readonly ChapterEntry[];
  /** The keys of the comments and book notes moved, to delete (MG7, MG10: a refused one stays). */
  readonly comments: readonly IDBValidKey[];
  readonly bookNotes: readonly IDBValidKey[];
}

/**
 * Table MG over every record of the three stores: comments first, then book notes, then entries (MG17), each record its
 * reader reads moved under its own ID, or `newId()`'s when a note already holds it (MG5, MG8, MG11); a record its reader
 * refuses is left where it is (MG4, MG7, MG10). `at` is the move's time.
 */
export function planMove(input: MoveInput, at: number, newId: () => string): MovePlan {
  const notes: NoteRecord[] = [];
  const held = new Set<string>();
  const free = (id: string): string => {
    let chosen = id;
    while (held.has(chosen)) chosen = newId();
    held.add(chosen);
    return chosen;
  };
  /** The record `raw` read with `read`, or undefined for one its reader refuses. */
  const readable = <T>(read: (value: unknown) => T, raw: unknown): T | undefined => {
    try {
      return read(raw);
    } catch {
      return undefined;
    }
  };
  const comments: IDBValidKey[] = [];
  for (const [key, raw] of input.comments) {
    const comment = readable(readCommentRecord, raw);
    if (comment === undefined) continue;
    notes.push(commentNote(free(comment.id), comment));
    comments.push(key);
  }
  const bookNotes: IDBValidKey[] = [];
  for (const [key, raw] of input.bookNotes) {
    const note = readable(readBookNote, raw);
    if (note === undefined) continue;
    notes.push(bookNoteNote(free(note.id), note));
    bookNotes.push(key);
  }
  const entries: ChapterEntry[] = [];
  for (const [, raw] of input.entries) {
    const legacy = readable(readLegacyEntry, raw);
    if (legacy === undefined) continue;
    entries.push(legacy.entry);
    const note = legacy.notes === '' ? null : chapterNotesNote(free(legacy.entry.id), legacy.entry, legacy.notes, at);
    if (note !== null) notes.push(note);
  }
  return { notes, entries, comments, bookNotes };
}
